import { useEffect, useState, useCallback } from 'react';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { payOrderApi } from '../utils/api';
import type { PayOrderInfo } from '../types/api';

const STATUS_STYLE: Record<number, { label: string; className: string }> = {
  0: { label: 'Pending', className: 'text-amber-400 bg-amber-400/10' },
  1: { label: 'Paid', className: 'text-emerald-400 bg-emerald-400/10' },
  2: { label: 'Refunded', className: 'text-zinc-400 bg-zinc-400/10' },
  3: { label: 'Closed', className: 'text-red-400 bg-red-400/10' },
};

const NOTIFY_STYLE: Record<number, { label: string; className: string }> = {
  0: { label: 'Pending', className: 'text-zinc-400 bg-zinc-400/10' },
  1: { label: 'Sent', className: 'text-emerald-400 bg-emerald-400/10' },
  2: { label: 'Failed', className: 'text-red-400 bg-red-400/10' },
};

const PaymentsPage: React.FC = () => {
  const [orders, setOrders] = useState<PayOrderInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage, setPerPage] = useState(15);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await payOrderApi.getList(page, filterStatus, searchTerm.trim());
      if (response.code === 1 && response.data) {
        setOrders(Array.isArray(response.data.data) ? response.data.data : []);
        setLastPage(response.data.last_page || 1);
        setTotal(response.data.total || 0);
        setPerPage(response.data.per_page || 15);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error('Failed to fetch pay orders:', error);
      setOrders([]);
    }
    setLoading(false);
  }, [page, filterStatus, searchTerm]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    setPage(1);
  }, [filterStatus, searchTerm]);

  const formatTime = (timestamp: number) => {
    if (!timestamp) return '-';
    return new Date(timestamp * 1000).toLocaleString();
  };

  const formatAmount = (amount: string, currency: string) => {
    const symbol = currency === 'USD' ? '$' : currency === 'CNY' ? '¥' : currency === 'EUR' ? '€' : '';
    return `${symbol}${amount} ${currency || ''}`.trim();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Payments</h1>
        <p className="text-zinc-400 mt-1">View payment orders generated by your applications</p>
      </div>

      {/* Filters */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by system order number..."
              className="w-full pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-600 transition-colors"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-zinc-600 transition-colors"
          >
            <option value="all">All Status</option>
            <option value="0">Pending</option>
            <option value="1">Paid</option>
            <option value="2">Refunded</option>
            <option value="3">Closed</option>
          </select>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left text-zinc-400 text-sm font-medium px-6 py-4">Order SN</th>
                <th className="text-left text-zinc-400 text-sm font-medium px-6 py-4">Merchant Order</th>
                <th className="text-left text-zinc-400 text-sm font-medium px-6 py-4">Subject</th>
                <th className="text-right text-zinc-400 text-sm font-medium px-6 py-4">Amount</th>
                <th className="text-left text-zinc-400 text-sm font-medium px-6 py-4">Status</th>
                <th className="text-left text-zinc-400 text-sm font-medium px-6 py-4">Notify</th>
                <th className="text-left text-zinc-400 text-sm font-medium px-6 py-4">Paid At</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-zinc-400">
                    Loading...
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-zinc-400">
                    No payment orders found
                  </td>
                </tr>
              ) : (
                orders.map((order) => {
                  const status = STATUS_STYLE[order.status] || {
                    label: String(order.status),
                    className: 'text-zinc-400 bg-zinc-400/10',
                  };
                  const notify = NOTIFY_STYLE[order.notify_status] || {
                    label: String(order.notify_status),
                    className: 'text-zinc-400 bg-zinc-400/10',
                  };
                  return (
                    <tr
                      key={order.id}
                      className="border-b border-zinc-800 last:border-0 hover:bg-zinc-800/50 transition-colors"
                    >
                      <td className="px-6 py-4 text-white text-sm font-mono">
                        <div className="truncate max-w-[200px]" title={order.order_sn}>
                          {order.order_sn}
                        </div>
                        <div className="text-zinc-500 text-xs">#{order.id}</div>
                      </td>
                      <td className="px-6 py-4 text-zinc-300 text-sm font-mono">
                        <div className="truncate max-w-[140px]" title={order.out_trade_no}>
                          {order.out_trade_no}
                        </div>
                        <div className="text-zinc-500 text-xs">app #{order.app_id}</div>
                      </td>
                      <td className="px-6 py-4 text-zinc-300 text-sm">
                        {order.subject || <span className="text-zinc-500">-</span>}
                      </td>
                      <td className="px-6 py-4 text-right text-white text-sm font-medium">
                        {formatAmount(order.amount, order.currency)}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${status.className}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${notify.className}`}
                          title={order.notify_remark || undefined}
                        >
                          {notify.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-zinc-400 text-sm">
                        {order.paytime_text || formatTime(order.paytime)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="text-zinc-400 text-sm">
          {total > 0 ? (
            <>
              Showing {(page - 1) * perPage + 1}-{Math.min(page * perPage, total)} of {total} orders
            </>
          ) : (
            'No orders'
          )}
        </div>
        {lastPage > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="flex items-center gap-1 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm transition-colors"
            >
              <ChevronLeft size={16} />
              Prev
            </button>
            <span className="text-zinc-400 text-sm px-2">
              Page {page} / {lastPage}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              disabled={page >= lastPage || loading}
              className="flex items-center gap-1 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm transition-colors"
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentsPage;
